import { useState } from 'react';
import { Button, Icon } from '@evertrue/et-components';
import EverTrue from 'app';
import OutreachTabGroup from '../outreach-tab-group';

/**
 * Mock data for bulk email campaigns.
 */
const MOCK_BULK_EMAILS = {
  scheduled: [
    {
      id: 1,
      name: 'Winter Appeal 2025',
      recipientCount: 3200,
      scheduledDate: 'Dec 15, 2025',
      scheduledTime: '9:00 AM',
    },
    {
      id: 2,
      name: 'Board Update - December',
      recipientCount: 45,
      scheduledDate: 'Dec 12, 2025',
      scheduledTime: '10:30 AM',
    },
    {
      id: 3,
      name: 'Volunteer Thank You',
      recipientCount: 892,
      scheduledDate: 'Dec 18, 2025',
      scheduledTime: '2:00 PM',
    },
  ],
  sent: [
    {
      id: 1,
      name: 'Annual Giving Campaign 2025',
      recipientCount: 1247,
      sendRate: 100,
      openRate: 23,
      clickRate: 4.2,
      repliesCount: 18,
      unresolvedReplies: 3,
      sentDate: 'Nov 20, 2025',
      sentTime: '2:30 PM',
    },
    {
      id: 2,
      name: 'Alumni Newsletter - Fall Edition',
      recipientCount: 3842,
      sendRate: 100,
      openRate: 19,
      clickRate: 2.8,
      repliesCount: 12,
      unresolvedReplies: 0,
      sentDate: 'Nov 15, 2025',
      sentTime: '9:00 AM',
    },
    {
      id: 3,
      name: 'Homecoming 2025 Invitation',
      recipientCount: 2156,
      sendRate: 100,
      openRate: 31,
      clickRate: 6.4,
      repliesCount: 45,
      unresolvedReplies: 8,
      sentDate: 'Nov 10, 2025',
      sentTime: '4:15 PM',
    },
    {
      id: 4,
      name: 'Year-End Giving Appeal',
      recipientCount: 5023,
      sendRate: 100,
      openRate: 16,
      clickRate: 2.1,
      repliesCount: 8,
      unresolvedReplies: 0,
      sentDate: 'Nov 1, 2025',
      sentTime: '11:00 AM',
    },
    {
      id: 5,
      name: 'Student Success Stories',
      recipientCount: 1890,
      sendRate: 100,
      openRate: 27,
      clickRate: 5.8,
      repliesCount: 23,
      unresolvedReplies: 6,
      sentDate: 'Oct 28, 2025',
      sentTime: '3:45 PM',
    },
  ],
};

/**
 * Bulk Email Controller - shows scheduled and sent bulk email campaigns.
 */
const BulkEmailController = () => {
  const [activeView, setActiveView] = useState('sent');
  const [bulkEmails] = useState(MOCK_BULK_EMAILS);

  const handleTabChange = (tab) => {
    EverTrue.UrlManager.navigate(`/outreach/${tab}`, true, false);
  };

  const handleViewCampaign = (id) => {
    EverTrue.Navigator(`/cadences-v2/bulk-message/${id}`, true);
  };

  const handleViewReplies = (id) => {
    EverTrue.Alert.info(`Opening replies for campaign ${id}...`);
  };

  const scheduledCount = bulkEmails.scheduled.length;
  const sentCount = bulkEmails.sent.length;

  return (
    <div className="bulk-email-controller">
      <div className="bulk-email-controller--header">
        <div className="bulk-email-controller--header-left">
          <h1 className="bulk-email-controller--title">Outreach</h1>
        </div>
        <Button type="default" onClick={() => EverTrue.Navigator('/cadences-v2/bulk-message/create', true)}>
          <Icon icon="add" /> New Bulk Email
        </Button>
      </div>

      <OutreachTabGroup activeTab="bulk-email" onTabChange={handleTabChange} />

      <div className="bulk-email-controller--content">
        <div className="bulk-email-controller--toolbar">
          <div className="bulk-email-controller--toggle">
            <button
              className={`bulk-email-controller--toggle-btn ${activeView === 'scheduled' ? 'is-active' : ''}`}
              onClick={() => setActiveView('scheduled')}
            >
              <Icon icon="schedule" />
              Scheduled
              <span className="bulk-email-controller--toggle-count">{scheduledCount}</span>
            </button>
            <button
              className={`bulk-email-controller--toggle-btn ${activeView === 'sent' ? 'is-active' : ''}`}
              onClick={() => setActiveView('sent')}
            >
              <Icon icon="check-circle" />
              Sent
              <span className="bulk-email-controller--toggle-count">{sentCount}</span>
            </button>
          </div>

          <div className="bulk-email-controller--filter">
            <Button type="secondary">
              <Icon icon="filter-list" />
              <span>Filter</span>
            </Button>
          </div>
        </div>

        {activeView === 'scheduled' && (
          <div className="bulk-email-controller--list">
            {bulkEmails.scheduled.map((email) => (
              <div
                key={email.id}
                className="bulk-email-controller--card bulk-email-controller--card-scheduled"
                onClick={() => handleViewCampaign(email.id)}
              >
                <div className="bulk-email-controller--card-icon">
                  <Icon icon="schedule" />
                </div>
                <div className="bulk-email-controller--card-content">
                  <div className="bulk-email-controller--card-header">
                    <h3 className="bulk-email-controller--card-name">{email.name}</h3>
                    <span className="bulk-email-controller--badge bulk-email-controller--badge-scheduled">
                      <Icon icon="schedule" /> Scheduled
                    </span>
                  </div>
                  <div className="bulk-email-controller--card-meta">
                    <span className="bulk-email-controller--meta-item">
                      <Icon icon="group" />
                      {email.recipientCount.toLocaleString()} recipients
                    </span>
                    <span className="bulk-email-controller--meta-item">
                      <Icon icon="event" />
                      {email.scheduledDate} at {email.scheduledTime}
                    </span>
                  </div>
                </div>
                <div className="bulk-email-controller--card-actions">
                  <Button type="simple">
                    <Icon icon="edit" />
                  </Button>
                  <Button type="simple">
                    <Icon icon="more-vert" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeView === 'sent' && (
          <div className="bulk-email-controller--list">
            {bulkEmails.sent.map((email) => (
              <div
                key={email.id}
                className="bulk-email-controller--card bulk-email-controller--card-sent"
                onClick={() => handleViewCampaign(email.id)}
              >
                <div className="bulk-email-controller--card-icon bulk-email-controller--card-icon-sent">
                  <Icon icon="email" />
                </div>
                <div className="bulk-email-controller--card-content">
                  <div className="bulk-email-controller--card-header">
                    <h3 className="bulk-email-controller--card-name">{email.name}</h3>
                    {email.unresolvedReplies > 0 ? (
                      <span className="bulk-email-controller--badge bulk-email-controller--badge-warning">
                        {email.unresolvedReplies} Unresolved
                      </span>
                    ) : (
                      <span className="bulk-email-controller--badge bulk-email-controller--badge-success">
                        <Icon icon="check" /> All Resolved
                      </span>
                    )}
                  </div>
                  <div className="bulk-email-controller--card-meta">
                    <span className="bulk-email-controller--meta-item">
                      <Icon icon="group" />
                      {email.recipientCount.toLocaleString()} recipients
                    </span>
                    <span className="bulk-email-controller--meta-item">
                      <Icon icon="event" />
                      {email.sentDate}
                    </span>
                  </div>
                  <div className="bulk-email-controller--metrics">
                    <div className="bulk-email-controller--metric">
                      <span className="bulk-email-controller--metric-value">{email.sendRate}%</span>
                      <span className="bulk-email-controller--metric-label">Delivered</span>
                      <div className="bulk-email-controller--metric-bar">
                        <div
                          className="bulk-email-controller--metric-fill bulk-email-controller--metric-fill-gray"
                          style={{ width: `${email.sendRate}%` }}
                        />
                      </div>
                    </div>
                    <div className="bulk-email-controller--metric">
                      <span className="bulk-email-controller--metric-value bulk-email-controller--metric-value-green">
                        {email.openRate}%
                      </span>
                      <span className="bulk-email-controller--metric-label">Opened</span>
                      <div className="bulk-email-controller--metric-bar">
                        <div
                          className="bulk-email-controller--metric-fill bulk-email-controller--metric-fill-green"
                          style={{ width: `${email.openRate}%` }}
                        />
                      </div>
                    </div>
                    <div className="bulk-email-controller--metric">
                      <span className="bulk-email-controller--metric-value bulk-email-controller--metric-value-blue">
                        {email.clickRate}%
                      </span>
                      <span className="bulk-email-controller--metric-label">Clicked</span>
                      <div className="bulk-email-controller--metric-bar">
                        <div
                          className="bulk-email-controller--metric-fill bulk-email-controller--metric-fill-blue"
                          style={{ width: `${Math.min(email.clickRate * 10, 100)}%` }}
                        />
                      </div>
                    </div>
                    <div className="bulk-email-controller--metric">
                      <span className="bulk-email-controller--metric-value">{email.repliesCount}</span>
                      <span className="bulk-email-controller--metric-label">Replies</span>
                    </div>
                  </div>
                </div>
                <div className="bulk-email-controller--card-actions">
                  <Button
                    type="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewReplies(email.id);
                    }}
                  >
                    View Replies
                  </Button>
                  <Button type="simple">
                    <Icon icon="more-vert" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkEmailController;
